import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import SEO from '../components/SEO';
import { getOptimizedImageUrl } from '../lib/seo';
import { 
  ShieldCheck, 
  Award, 
  Users, 
  Clock, 
  ArrowRight, 
  Phone, 
  Mail, 
  MapPin, 
  Sparkles, 
  CheckCircle2, 
  Building,
  Home as HomeIcon,
  Wrench,
  Paintbrush,
  Palette,
  Hammer,
  Check,
  Star,
  Quote,
  Briefcase,
  Receipt,
  Coins,
  ExternalLink
} from 'lucide-react';
import ReviewSlider from '../components/ReviewSlider';
import ContactForm from '../components/ContactForm';

const coreValues = [
  {
    icon: ShieldCheck,
    number: '01',
    title: 'Kompromisløs kvalitet',
    desc: 'Vi går aldrig på kompromis med materialer eller udførelse. Vores arbejde står knivskarpt og sikrer langvarig holdbarhed.',
  },
  {
    icon: Award,
    number: '02',
    title: 'Ordentlighed & præcision',
    desc: 'Vi dækker grundigt af, rydder pænt op efter os og overholder altid vores aftaler og tidsplaner til punkt og prikke.',
  },
  {
    icon: Users,
    number: '03',
    title: 'Faglærte malersvende',
    desc: 'Vores team består udelukkende af uddannede malere og talentfulde lærlinge med stor faglig stolthed og passion.',
  },
  {
    icon: Clock,
    number: '04',
    title: 'Knap 70 års erfaring',
    desc: 'Siden 1957 har vi oparbejdet en unik know-how og ekspertise, der sikrer dig det absolut bedste resultat hver gang.',
  },
];

const completeSolutions = [
  {
    icon: Palette,
    title: 'Maling af vægge',
    desc: 'Sublim indendørs finish med fokus på præcision, dækkeevne og harmoniske farvevalg.',
    href: '/ydelser/maling-af-vaegge'
  },
  {
    icon: Hammer,
    title: 'Spartling af vægge',
    desc: 'Fuldspartling og klargøring af vægge, der sikrer det perfekte, glatte underlag.',
    href: '/ydelser/spartling-af-vaegge'
  },
  {
    icon: ShieldCheck,
    title: 'Opsætning af filt',
    desc: 'Professionel opsætning af filt og glasvæv for øget holdbarhed og en ensartet overflade.',
    href: '/ydelser/opsaetning-af-filt'
  },
  {
    icon: Sparkles,
    title: 'Opsætning af tapet',
    desc: 'Præcis opsætning af moderne mønstre, klassisk savsmuldstapet eller eksklusive tekstiler.',
    href: '/ydelser/tapet'
  },
  {
    icon: Paintbrush,
    title: 'Udvendigt malerarbejde',
    desc: 'Professionel beskyttelse og fornyelse af facader, træværk, vinduer og døre mod det danske vejr.',
    href: '/ydelser/udvendigt-malerarbejde'
  }
];

const featuredCases = [
  {
    title: 'NORMAL',
    client: 'Torben Mouritsen, Adm. Direktør',
    quote: 'BM Maler kender huset som deres egen bukselomme. De arbejder effektivt uden at gå på kompromis med kvaliteten.',
    desc: 'Løbende vedligeholdelse af NORMALS 3.600 kvm store kontordomicil i Skanderborg - både udendørs & indendørs malerarbejde.',
    href: '/normal',
    image: 'https://assets.auxodev.dk/assets/b3b17412-91e4-45b1-be58-3b3c80af2fb2/indendoers-malerarbejde-normal.jpg',
    alt: 'Maler fra BM Maler i færd med indendørs malerarbejde i NORMALs kontordomicil i Skanderborg',
    badge: 'Erhverv & Domicil'
  },
  {
    title: 'Wine Mage',
    client: 'Per Madsbøll Pedersen, Direktør',
    quote: 'De har virkelig hjulpet mig med at træffe de rigtige valg i forhold til farvenuancer og overfladebehandling.',
    desc: 'Unikt, stilfuldt og rustikt showroom i My Garage i Vejle med fokus på sanser og detaljer.',
    href: '/wine-mage',
    image: 'https://assets.auxodev.dk/assets/b3b17412-91e4-45b1-be58-3b3c80af2fb2/wine-mage-vejle-bm-maler.jpg',
    alt: 'Wine Mage i Vejle efter fuldendt malerarbejde fra BM Maler',
    badge: 'Showroom & Bejdsning'
  },
  {
    title: 'Formula',
    client: 'Kevin Rossel, Leasingansvarlig',
    quote: 'BM Maler har givet den gas på finishen og udført malerarbejdet med en sjælden passion for detaljen.',
    desc: 'Eksklusivt showroom for Ferrari og Maserati i Vejle. Det mørke og elegante look matcher bilerne.',
    href: '/formula',
    image: 'https://assets.auxodev.dk/assets/b3b17412-91e4-45b1-be58-3b3c80af2fb2/formula-vejle-logo.jpg',
    alt: 'Logo for Formula i Vejle',
    badge: 'High-end Showroom'
  },
];

// Animation presets
const fadeInUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { 
    opacity: 1, 
    y: 0, 
    transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] } 
  }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.12,
      delayChildren: 0.05
    }
  }
};

const scaleIn = {
  hidden: { opacity: 0, scale: 0.95 },
  visible: { 
    opacity: 1, 
    scale: 1, 
    transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] } 
  }
};

export default function Home() {
  const [activeCase, setActiveCase] = useState(0);

  return (
    <div className="bg-[#fafaf9] text-slate-900 min-h-screen font-sans overflow-hidden">
      <SEO />
      
      {/* SECTION 1: Panoramic Premium Hero Section */}
      <section className="relative min-h-[90vh] flex items-center bg-slate-950 text-white pt-24 pb-44 overflow-hidden">
        {/* Panoramic Background Image */}
        <div className="absolute inset-0 z-0">
          <motion.img
            initial={{ scale: 1.25, opacity: 0 }}
            animate={{ scale: 1.12, opacity: 0.45 }}
            transition={{ duration: 1.8, ease: "easeOut" }}
            src={getOptimizedImageUrl("https://assets.auxodev.dk/assets/b3b17412-91e4-45b1-be58-3b3c80af2fb2/malerarbejde-bm-maler.jpg", { width: 1600, quality: 80 })}
            alt="Maler fra BM Maler med malerudstyr i færdigmalet rum"
            width="1600"
            height="900"
            fetchPriority="high"
            className="w-full h-full object-cover object-[75%_center] translate-x-4 sm:translate-x-8 md:translate-x-12"
          />
          {/* Left-to-right dark gradient overlay to prevent text washout */}
          <div className="absolute inset-0 bg-gradient-to-r from-slate-950/95 via-slate-950/75 to-transparent" />
          
          {/* Subtle grid pattern overlay */}
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff05_1px,transparent_1px),linear-gradient(to_bottom,#ffffff05_1px,transparent_1px)] bg-[size:4rem_4rem]" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <motion.div 
            variants={staggerContainer}
            initial="hidden"
            animate="visible"
            className="max-w-3xl space-y-8"
          >
            <motion.div 
              variants={fadeInUp}
              className="inline-flex items-center gap-2 bg-white/10 border border-white/20 backdrop-blur-md px-4 py-1.5 rounded-full text-[#F1B528] text-xs sm:text-sm font-bold uppercase tracking-wider"
            >
              <Sparkles className="w-4 h-4 text-[#F1B528]" />
              Knap 70 års uovertruffent malerhåndværk
            </motion.div>
            
            <motion.h1 
              variants={fadeInUp}
              className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.1] text-white"
            >
              Malerarbejde i særklasse, der <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#F1B528] to-white">transformerer dine rum</span>
            </motion.h1>
            
            <motion.p 
              variants={fadeInUp}
              className="text-base sm:text-lg text-slate-300 leading-relaxed max-w-2xl"
            >
              Vi forener stolte håndværkstraditioner med moderne teknikker. Hos BM Maler leverer vores faglærte team kompromisløs kvalitet og gennemskuelige løsninger til både private hjem og erhvervsejendomme.
            </motion.p>

            {/* Trust Indicators */}
            <motion.div 
              variants={fadeInUp}
              className="grid grid-cols-3 gap-6 pt-6 border-t border-white/10 max-w-xl"
            >
              <div className="space-y-1">
                <span className="block text-[#F1B528] text-2xl font-bold">1957</span>
                <span className="block text-[10px] text-slate-400 font-bold uppercase tracking-wider">Grundlagt</span>
              </div>
              <div className="space-y-1">
                <span className="block text-[#F1B528] text-2xl font-bold">100%</span>
                <span className="block text-[10px] text-slate-400 font-bold uppercase tracking-wider">Faglært Team</span>
              </div>
              <div className="space-y-1">
                <span className="block text-[#F1B528] text-2xl font-bold">Garanti</span>
                <span className="block text-[10px] text-slate-400 font-bold uppercase tracking-wider">Danske Malermestre</span>
              </div>
            </motion.div>

            <motion.div 
              variants={fadeInUp}
              className="flex flex-wrap gap-4 pt-4"
            >
              <a
                href="#kontaktformular"
                className="bg-[#F1B528] hover:bg-[#D99C1B] text-slate-950 px-8 py-4 rounded-full text-sm font-bold transition-all duration-300 shadow-lg shadow-[#F1B528]/10 flex items-center gap-2 group cursor-pointer"
              >
                Kontakt os
                <ArrowRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-1" />
              </a>
              <a
                href="tel:+4575695900"
                aria-label="Ring til BM Maler på 75 69 59 00"
                className="bg-white/5 hover:bg-white/10 text-[#F1B528] hover:text-[#D99C1B] border border-[#F1B528]/30 px-8 py-4 rounded-full text-sm font-bold transition-all duration-300 backdrop-blur-sm flex items-center gap-2"
              >
                Ring: 75 69 59 00
              </a>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* SECTION 2: Overlapping Service Cards Grid wrapped in a dominant Warm Sand background */}
      <div className="bg-[#F1B528] pt-1 pb-16">
        <section className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-28">
          <motion.div 
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-50px" }}
            className="grid grid-cols-1 md:grid-cols-12 gap-6 lg:gap-8"
          >
            
            {/* Card 1: Privat */}
            <motion.div 
              variants={fadeInUp}
              whileHover={{ y: -8, transition: { duration: 0.3 } }}
              className="flex flex-col rounded-3xl overflow-hidden shadow-2xl shadow-slate-950/20 border border-white/10 h-full md:col-span-6 lg:col-span-4"
            >
              {/* Top Half: Frosted Glass */}
              <div className="bg-slate-950/85 backdrop-blur-md text-white p-8 space-y-4 flex-1">
                <div className="inline-flex p-3 rounded-full bg-white/10 text-[#F1B528]">
                  <HomeIcon className="w-6 h-6" />
                </div>
                <h2 className="text-xl font-bold tracking-tight">Privat</h2>
                <p className="text-sm text-slate-300 leading-relaxed">
                  Vi respekterer dit hjem og leverer en uovertruffen finish. Fra stuer til udendørs træværk, sikrer vi, at kvaliteten sidder lige i skabet.
                </p>
              </div>
              {/* Bottom Half: Solid Warm Sand */}
              <div className="bg-[#fafaf9] text-slate-950 p-8 flex flex-col justify-between items-start gap-4">
                <ul className="space-y-2 text-xs font-semibold text-slate-700">
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-[#D99C1B] shrink-0" /> Fuld respekt for dit private hjem
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-[#D99C1B] shrink-0" /> Kompromisløs kvalitet og finish
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-[#D99C1B] shrink-0" /> Uddannede malere uden akkord
                  </li>
                </ul>
                <RouterLink
                  to="/ydelser/private"
                  className="inline-flex items-center gap-2 bg-[#F1B528] hover:bg-[#D99C1B] text-slate-950 px-5 py-2.5 rounded-full text-xs font-bold transition-all duration-300 shadow-sm"
                >
                  Læs mere om Privat
                  <ArrowRight className="w-3.5 h-3.5" />
                </RouterLink>
              </div>
            </motion.div>

            {/* Card 2: Erhverv */}
            <motion.div 
              variants={fadeInUp}
              whileHover={{ y: -8, transition: { duration: 0.3 } }}
              className="flex flex-col rounded-3xl overflow-hidden shadow-2xl shadow-slate-950/20 border border-white/10 h-full md:col-span-6 lg:col-span-4"
            >
              {/* Top Half: Frosted Glass */}
              <div className="bg-slate-950/85 backdrop-blur-md text-white p-8 space-y-4 flex-1">
                <div className="inline-flex p-3 rounded-full bg-white/10 text-[#F1B528]">
                  <Building className="w-6 h-6" />
                </div>
                <h2 className="text-xl font-bold tracking-tight">Erhverv</h2>
                <p className="text-sm text-slate-300 leading-relaxed">
                  Vi har kapaciteten og erfaringen til at styre større malerentrepriser i mål til aftalt tid. Vi sikrer rettidig kommunikation og stærkt samarbejde.
                </p>
              </div>
              {/* Bottom Half: Solid Warm Sand */}
              <div className="bg-[#fafaf9] text-slate-950 p-8 flex flex-col justify-between items-start gap-4">
                <ul className="space-y-2 text-xs font-semibold text-slate-700">
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-[#D99C1B] shrink-0" /> Overholdelse af tidsplaner
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-[#D99C1B] shrink-0" /> Erfaring med store entrepriser
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-[#D99C1B] shrink-0" /> Professionelt og moderne udstyr
                  </li>
                </ul>
                <RouterLink
                  to="/ydelser/erhverv"
                  className="inline-flex items-center gap-2 bg-[#F1B528] hover:bg-[#D99C1B] text-slate-950 px-5 py-2.5 rounded-full text-xs font-bold transition-all duration-300 shadow-sm"
                >
                  Læs mere om Erhverv
                  <ArrowRight className="w-3.5 h-3.5" />
                </RouterLink>
              </div>
            </motion.div>

            {/* Card 3: Vedligeholdelse */}
            <motion.div 
              variants={fadeInUp}
              whileHover={{ y: -8, transition: { duration: 0.3 } }}
              className="flex flex-col rounded-3xl overflow-hidden shadow-2xl shadow-slate-950/20 border border-white/10 h-full md:col-span-12 lg:col-span-4"
            >
              {/* Top Half: Frosted Glass (Dark Slate) */}
              <div className="bg-slate-900 text-white p-8 space-y-4 flex-1">
                <div className="inline-flex p-3 rounded-full bg-white/5 text-[#F1B528]">
                  <Wrench className="w-6 h-6" />
                </div>
                <h2 className="text-xl font-bold tracking-tight">Vedligeholdelse</h2>
                <p className="text-sm text-slate-300 leading-relaxed">
                  Vi forlænger levetiden på dit træværk, dine vinduer og dine facader. Vedligeholdelse er en investering i din ejendoms værdi.
                </p>
              </div>
              {/* Bottom Half: Solid White */}
              <div className="bg-white text-slate-900 p-8 flex flex-col justify-between items-start gap-4">
                <ul className="space-y-2 text-xs font-semibold text-slate-600">
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-[#D99C1B] shrink-0" /> Maling af vinduer og døre
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-[#D99C1B] shrink-0" /> Fuger og revner udbedres
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-[#D99C1B] shrink-0" /> Langvarig beskyttelse mod vejr
                  </li>
                </ul>
                <RouterLink
                  to="/ydelser/vedligeholdelsesarbejde"
                  className="inline-flex items-center gap-2 bg-slate-950 hover:bg-[#F1B528] hover:text-slate-950 text-white px-5 py-2.5 rounded-full text-xs font-bold transition-all duration-300 shadow-sm"
                >
                  Læs mere om Vedligehold
                  <ArrowRight className="w-3.5 h-3.5" />
                </RouterLink>
              </div>
            </motion.div>

          </motion.div>
        </section>
      </div>

      {/* SECTION 3: Organic Wave Section Transition (From Pinterest Spec) */}
      <div className="w-full overflow-hidden leading-[0] bg-[#F1B528]">
        <svg
          viewBox="0 0 1200 120"
          preserveAspectRatio="none"
          className="relative block w-full h-[80px] text-white fill-current"
        >
          <path d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V120H0V0C26.9,4.75,55.05,12.72,83.1,20.11,135.66,32.91,186.86,45.4,239.52,51.7,266.9,55,294.3,56.5,321.39,56.44Z"></path>
        </svg>
      </div>

      {/* SECTION 4: "Complete Painting Solutions" Services Grid (From Pinterest Spec) */}
      <section className="bg-white py-24 sm:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-3xl mx-auto mb-20 space-y-4"
          >
            <span className="text-[#D99C1B] text-xs font-bold uppercase tracking-wider bg-[#F1B528]/15 border border-[#F1B528]/30 px-4 py-1.5 rounded-full">
              Vores Ydelser
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 tracking-tight pt-6">
              Komplette malerløsninger
            </h2>
            <p className="text-slate-500 text-sm sm:text-base max-w-2xl mx-auto">
              Vi dækker alle behov inden for malerfaget med fokus på ordentlighed, kvalitet og sublim finish.
            </p>
          </motion.div>

          {/* Minimalist Grid relying on white space (Pinterest spec) */}
          <motion.div 
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-50px" }}
            className="grid grid-cols-1 md:grid-cols-12 gap-y-16 gap-x-12 max-w-6xl mx-auto"
          >
            {completeSolutions.map((sol, idx) => {
              const Icon = sol.icon;
              const isThirdCard = (idx + 1) % 3 === 0;
              return (
                <motion.div
                  key={idx}
                  variants={fadeInUp}
                  whileHover={{ y: -6, transition: { duration: 0.3 } }}
                  className={`${isThirdCard ? 'md:col-span-12' : 'md:col-span-6'} lg:col-span-4`}
                >
                  <RouterLink
                    to={sol.href}
                    className="flex flex-col items-center text-center space-y-4 group p-8 rounded-3xl border border-slate-100 bg-white hover:border-[#F1B528]/40 hover:shadow-xl transition-all duration-300 cursor-pointer h-full"
                  >
                    <div className="inline-flex p-4 rounded-full bg-[#fafaf9] text-[#D99C1B] group-hover:bg-[#F1B528] group-hover:text-slate-950 transition-all duration-300 shadow-sm">
                      <Icon className="w-7 h-7 stroke-[1.5]" />
                    </div>
                    <h3 className="text-lg font-bold text-slate-900 tracking-tight group-hover:text-[#D99C1B] transition-colors">
                      {sol.title}
                    </h3>
                    <div className="w-12 h-[2px] bg-[#F1B528] transition-all duration-300 group-hover:w-20" />
                    <p className="text-sm text-slate-500 leading-relaxed max-w-xs">
                      {sol.desc}
                    </p>
                  </RouterLink>
                </motion.div>
              );
            })}
          </motion.div>

          <motion.div 
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3, duration: 0.6 }}
            className="text-center pt-16"
          >
            <RouterLink
              to="/ydelser"
              className="inline-flex items-center gap-2 border-2 border-slate-900 hover:border-[#F1B528] hover:bg-[#F1B528] text-slate-900 hover:text-slate-950 px-8 py-3.5 rounded-full text-sm font-bold transition-all duration-300"
            >
              Se alle vores ydelser
              <ArrowRight className="w-4 h-4" />
            </RouterLink>
          </motion.div>
        </div>
      </section>

      {/* SECTION 4.5: Organic Wave Section Transition to Profile Section */}
      <div className="w-full overflow-hidden leading-[0] bg-white">
        <svg
          viewBox="0 0 1200 120"
          preserveAspectRatio="none"
          className="relative block w-full h-[80px] text-[#fafaf9] fill-current"
        >
          <path d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V120H0V0C26.9,4.75,55.05,12.72,83.1,20.11,135.66,32.91,186.86,45.4,239.52,51.7,266.9,55,294.3,56.5,321.39,56.44Z"></path>
        </svg>
      </div>

      {/* SECTION 5: Profile & History with dominant Warm Sand theme */}
      <section className="py-24 sm:py-32 bg-[#fafaf9]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
            <motion.div 
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="lg:col-span-6 space-y-6"
            >
              <div className="inline-flex items-center gap-2 bg-[#F1B528]/20 border border-[#F1B528]/40 px-3 py-1 rounded-full text-[#D99C1B] text-xs font-bold uppercase tracking-wider">
                Hvem er vi?
              </div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 tracking-tight leading-tight pt-6">
                Maler med knap 70 års erfaring med fokus på ordentlighed
              </h2>
              <p className="text-slate-600 leading-relaxed text-base">
                BM Maler A/S har siden starten tilbage i 1957 været en familiedrevet malerforretning. Vi hylder det gode håndværk og værner om den kompromisløse kvalitet.
              </p>
              <p className="text-slate-600 leading-relaxed text-sm">
                Vores team består af faglærte malersvende og talentfulde lærlinge, der alle sætter en ære i at udføre et professionelt stykke malerarbejde. For os er det afgørende, at du altid er 100 % tilfreds med resultatet.
              </p>
              <div className="pt-4">
                <RouterLink
                  to="/profil"
                  className="inline-flex items-center gap-2 bg-[#F1B528] hover:bg-[#D99C1B] text-slate-950 px-6 py-3.5 rounded-full text-sm font-bold transition-all duration-300 shadow-lg shadow-[#F1B528]/10 group"
                >
                  Læs mere om vores historie og profil
                  <ArrowRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-1" />
                </RouterLink>
              </div>
            </motion.div>

            <motion.div 
              variants={staggerContainer}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="lg:col-span-6 grid grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-8"
            >
              {coreValues.map((val, idx) => (
                <motion.div 
                  key={idx} 
                  variants={fadeInUp}
                  whileHover={{ y: -6, transition: { duration: 0.3 } }}
                  className="relative bg-white border border-slate-100 p-8 rounded-3xl space-y-4 shadow-md hover:border-[#F1B528]/60 transition-all duration-300 group"
                >
                  {/* Large background number */}
                  <span className="absolute top-4 right-6 text-5xl font-black text-slate-100 select-none group-hover:text-[#fafaf9] transition-colors duration-300">
                    {val.number}
                  </span>
                  <div className="inline-flex p-3 rounded-full bg-[#fafaf9] text-[#D99C1B] group-hover:bg-[#F1B528] group-hover:text-slate-950 transition-all duration-300 shadow-sm">
                    <val.icon className="w-5 h-5" />
                  </div>
                  <h3 className="text-base font-bold text-slate-900 pt-2">{val.title}</h3>
                  <p className="text-xs text-slate-500 leading-relaxed">{val.desc}</p>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* SECTION 5.2: Premium Quote Section - Lene Møller Thisvad */}
      <section className="py-24 bg-white border-t border-slate-100 relative overflow-hidden">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-[#F1B528]/5 rounded-full blur-3xl -translate-y-1/2" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-slate-100 rounded-full blur-3xl translate-y-1/2" />
        
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            
            {/* Image Column */}
            <motion.div 
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="lg:col-span-5"
            >
              <div className="relative">
                {/* Decorative background shape */}
                <div className="absolute -inset-4 rounded-3xl bg-gradient-to-tr from-[#F1B528] to-amber-100 opacity-30 blur-lg -z-10" />
                
                {/* Main Image Frame */}
                <div className="aspect-[4/5] rounded-3xl overflow-hidden shadow-2xl border-4 border-white bg-slate-100 relative group max-w-md mx-auto md:max-w-xs lg:max-w-none">
                  <img 
                    src={getOptimizedImageUrl("https://assets.auxodev.dk/assets/b3b17412-91e4-45b1-be58-3b3c80af2fb2/lene-moeller-thisvad-bm-maler.jpg", { width: 600, quality: 80 })} 
                    alt="Lene Møller Thisvad - Maler og Partner hos BM Maler" 
                    width="480"
                    height="600"
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-cover object-center transition-transform duration-700 group-hover:scale-105"
                  />
                  {/* Elegant overlay gradient */}
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-950/40 via-transparent to-transparent" />
                </div>
                
                {/* Floating Badge */}
                <div className="absolute -bottom-6 -right-6 bg-[#F1B528] text-slate-950 px-6 py-4 rounded-2xl shadow-xl border-2 border-white hidden sm:block">
                  <span className="block text-xs font-bold uppercase tracking-wider opacity-80">Maler & Partner</span>
                  <span className="block text-lg font-black tracking-tight">BM Maler</span>
                </div>
              </div>
            </motion.div>
            
            {/* Quote Column */}
            <motion.div 
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.1 }}
              className="lg:col-span-7 space-y-8"
            >
              <div className="space-y-4">
                <span className="inline-flex items-center gap-2 text-[#D99C1B] text-xs font-bold uppercase tracking-wider bg-[#F1B528]/10 border border-[#F1B528]/20 px-3 py-1.5 rounded-full">
                  <Quote className="w-3.5 h-3.5 fill-current" />
                  Faglig Stolthed & Tillid
                </span>
                <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight leading-tight">
                  Lad os forlænge levetiden
                </h2>
              </div>
              
              <div className="relative">
                {/* Large decorative quote mark */}
                <span className="absolute -top-12 -left-6 text-8xl font-serif text-[#F1B528]/20 select-none pointer-events-none">
                  “
                </span>
                
                <blockquote className="text-lg sm:text-xl text-slate-700 leading-relaxed font-medium italic relative z-10 pl-4 border-l-4 border-[#F1B528]">
                  ”Det er en tillidssag, når folk lukker os ind i deres hjem eller bygninger for at lave et stykke arbejde. Derfor arbejder vi med største professionalisme, når vi laver vedligeholdelsesarbejde og forlænger levetiden på eksempelvis facaden, trævinduerne, udhænget eller gavlen.”
                </blockquote>
              </div>
              
              <div className="pt-4 border-t border-slate-100 flex items-center gap-4">
                <div>
                  <cite className="not-italic block text-base font-bold text-slate-900">
                    Lene Møller Thisvad
                  </cite>
                  <span className="block text-xs text-slate-500 font-semibold uppercase tracking-wider mt-0.5">
                    Maler og Partner, BM Maler
                  </span>
                </div>
              </div>
            </motion.div>
            
          </div>
        </div>
      </section>

      {/* SECTION 5.5: Organic Wave Section Transition to Case Study Section */}
      <div className="w-full overflow-hidden leading-[0] bg-[#fafaf9]">
        <svg
          viewBox="0 0 1200 120"
          preserveAspectRatio="none"
          className="relative block w-full h-[80px] text-white fill-current"
        >
          <path d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V120H0V0C26.9,4.75,55.05,12.72,83.1,20.11,135.66,32.91,186.86,45.4,239.52,51.7,266.9,55,294.3,56.5,321.39,56.44Z"></path>
        </svg>
      </div>

      {/* SECTION 6: Premium Magazine-style Case Study Spotlight */}
      <section className="bg-white py-24 sm:py-32 border-y border-slate-100 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center mb-16">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="lg:col-span-6 space-y-4"
            >
              <span className="text-[#D99C1B] text-xs font-bold uppercase tracking-wider bg-[#F1B528]/20 border border-[#F1B528]/40 px-3 py-1 rounded-full">
                Udvalgte Cases
              </span>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 tracking-tight pt-6">
                Vi skaber resultater, der taler for sig selv
              </h2>
              <p className="text-slate-500 text-sm sm:text-base leading-relaxed pt-2">
                Som malerfirma med base i Vejle har vi kunder i hele Trekantsområdet. Vi håndterer alle opgaver, store som små, med samme fokus på kvalitet og ordentlighed.
              </p>
            </motion.div>
            <motion.div 
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="lg:col-span-6 lg:text-right"
            >
              <RouterLink
                to="/kundecases"
                className="inline-flex items-center gap-2 bg-slate-950 hover:bg-[#F1B528] hover:text-slate-950 text-white px-6 py-3.5 rounded-full text-sm font-bold transition-all duration-300 shadow-sm"
              >
                Se alle kundecases
                <ArrowRight className="w-4 h-4" />
              </RouterLink>
            </motion.div>
          </div>

          {/* Interactive Case Showcase */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-stretch">
            {/* Case Selector Buttons */}
            <motion.div 
              variants={staggerContainer}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="lg:col-span-4 flex flex-col gap-3 justify-center"
            >
              {featuredCases.map((item, idx) => (
                <motion.button
                  variants={fadeInUp}
                  key={idx}
                  onClick={() => setActiveCase(idx)}
                  className={`text-left p-6 rounded-3xl border transition-all duration-300 cursor-pointer flex flex-col gap-1 ${
                    activeCase === idx
                      ? 'bg-white border-[#F1B528] shadow-lg'
                      : 'bg-transparent border-slate-100 hover:bg-white/50'
                  }`}
                >
                  <span className="text-[10px] font-bold text-[#D99C1B] uppercase tracking-wider">{item.badge}</span>
                  <span className="text-lg font-bold text-slate-900">{item.title}</span>
                </motion.button>
              ))}
            </motion.div>

            {/* Active Case Detail Card */}
            <motion.div 
              variants={scaleIn}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="lg:col-span-8 bg-white border border-slate-100 rounded-3xl p-8 sm:p-12 shadow-md flex flex-col justify-between"
            >
              <AnimatePresence mode="wait">
                <motion.div 
                  key={activeCase}
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -15 }}
                  transition={{ duration: 0.4 }}
                  className="grid grid-cols-1 md:grid-cols-12 gap-8 items-center"
                >
                  <div className="md:col-span-7 space-y-6">
                    <div className="inline-flex bg-[#fafaf9] text-[#D99C1B] text-xs font-bold px-3 py-1 rounded-full">
                      {featuredCases[activeCase].badge}
                    </div>
                    <h3 className="text-2xl sm:text-3xl font-bold text-slate-900">
                      {featuredCases[activeCase].title}
                    </h3>
                    <p className="text-slate-600 leading-relaxed text-sm">
                      {featuredCases[activeCase].desc}
                    </p>
                    
                    {/* Quote Block */}
                    <div className="border-l-4 border-[#F1B528] pl-4 py-1 italic text-slate-700 bg-[#fafaf9] p-4 rounded-r-2xl">
                      <p className="text-xs leading-relaxed">"{featuredCases[activeCase].quote}"</p>
                      <span className="block text-[10px] font-bold text-slate-500 mt-2">— {featuredCases[activeCase].client}</span>
                    </div>
                  </div>

                  <div className="md:col-span-5">
                    <div className="aspect-[4/3] sm:aspect-square rounded-3xl overflow-hidden bg-[#fafaf9] border border-slate-100">
                      <img
                        src={getOptimizedImageUrl(featuredCases[activeCase].image, { width: 800, quality: 80 })}
                        alt={featuredCases[activeCase].alt || `BM Maler case - ${featuredCases[activeCase].title}`}
                        width="800"
                        height="600"
                        loading="lazy"
                        decoding="async"
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </div>
                </motion.div>
              </AnimatePresence>

              <div className="pt-8 border-t border-slate-100 mt-8 flex justify-between items-center">
                <span className="text-xs text-slate-400 font-semibold">BM Maler Case Study</span>
                <RouterLink
                  to={featuredCases[activeCase].href}
                  className="inline-flex items-center gap-2 bg-[#F1B528] hover:bg-[#D99C1B] text-slate-950 px-5 py-2.5 rounded-full text-xs font-bold transition-all duration-300 shadow-sm"
                >
                  Læs hele casen
                  <ArrowRight className="w-3.5 h-3.5" />
                </RouterLink>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* SECTION 6.5: Practical Info, Pricing & Careers (New Section) */}
      <section className="py-24 sm:py-32 bg-[#fafaf9] border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-3xl mx-auto mb-20 space-y-4"
          >
            <span className="text-[#D99C1B] text-xs font-bold uppercase tracking-wider bg-[#F1B528]/15 border border-[#F1B528]/30 px-4 py-1.5 rounded-full">
              Praktisk Info & Karriere
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 tracking-tight pt-6">
              Priser, fradrag og jobmuligheder hos BM Maler
            </h2>
            <p className="text-slate-500 text-sm sm:text-base max-w-2xl mx-auto">
              Få hurtigt overblik over vores prisstruktur, gældende skatteregler og hvordan du bliver en del af vores faglærte malerteam.
            </p>
          </motion.div>

          <motion.div 
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-50px" }}
            className="grid grid-cols-1 md:grid-cols-12 gap-8 max-w-6xl mx-auto"
          >
            
            {/* Card 1: Hvad koster en maler */}
            <motion.div 
              variants={fadeInUp}
              whileHover={{ y: -6, transition: { duration: 0.3 } }}
              className="bg-white border border-slate-100 rounded-3xl p-8 shadow-md hover:border-[#F1B528]/60 transition-all duration-300 flex flex-col justify-between group md:col-span-6 lg:col-span-6"
            >
              <div className="space-y-6">
                <div className="inline-flex p-4 rounded-full bg-[#fafaf9] text-[#D99C1B] group-hover:bg-[#F1B528] group-hover:text-slate-950 transition-all duration-300 shadow-sm">
                  <Coins className="w-6 h-6 stroke-[1.5]" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 tracking-tight pt-2">
                  Hvad koster en maler?
                </h3>
                <p className="text-sm text-slate-600 leading-relaxed">
                  Hos BM Maler får du professionelt dansk malerhåndværk udført af faglærte malere med mange års erfaring. Prisen på professionelt malerarbejde afhænger af opgavens omfang, materialer og tidsforbrug. Vi leverer altid gennemskuelige, skræddersyede tilbud baseret på høj faglighed og ordentlighed.
                </p>
                <div className="bg-amber-50 border-l-4 border-[#F1B528] p-4 rounded-r-2xl">
                  <p className="text-xs font-bold text-amber-900">
                    Bemærk angående kørsel:
                  </p>
                  <p className="text-xs text-amber-800 mt-1 leading-relaxed">
                    Da vi dækker et stort geografisk område og kører ud til kunder i hele lokalområdet, bliver der pålagt kørsel på alle vores opgaver.
                  </p>
                </div>
              </div>
              <div className="pt-8 mt-8 border-t border-slate-100">
                <RouterLink
                  to="/hvad-koster-en-maler"
                  className="inline-flex items-center gap-2 bg-[#F1B528] hover:bg-[#D99C1B] text-slate-950 px-5 py-2.5 rounded-full text-xs font-bold transition-all duration-300 shadow-sm"
                >
                  Se priser & prisstruktur
                  <ArrowRight className="w-3.5 h-3.5" />
                </RouterLink>
              </div>
            </motion.div>

            {/* Card 2: Ledige stillinger */}
            <motion.div 
              variants={fadeInUp}
              whileHover={{ y: -6, transition: { duration: 0.3 } }}
              className="bg-white border border-slate-100 rounded-3xl p-8 shadow-md hover:border-[#F1B528]/60 transition-all duration-300 flex flex-col justify-between group md:col-span-6 lg:col-span-6"
            >
              <div className="space-y-6">
                <div className="inline-flex p-4 rounded-full bg-[#fafaf9] text-[#D99C1B] group-hover:bg-[#F1B528] group-hover:text-slate-950 transition-all duration-300 shadow-sm">
                  <Briefcase className="w-6 h-6 stroke-[1.5]" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 tracking-tight pt-2">
                  Ledige stillinger
                </h3>
                <p className="text-sm text-slate-600 leading-relaxed">
                  Vil du være en del af et professionelt malerfirma i Vejle med fokus på ordentlighed, høj faglighed og et sundt arbejdsmiljø? Vi søger løbende dygtige faglærte malersvende og engagerede lærlinge.
                </p>
                <ul className="space-y-2 text-xs text-slate-500">
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-[#F1B528] shrink-0" /> Attraktive arbejdstider & timeløn
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-[#F1B528] shrink-0" /> Godt arbejdsmiljø & massageordning
                  </li>
                </ul>
              </div>
              <div className="pt-8 mt-8 border-t border-slate-100">
                <RouterLink
                  to="/super-svende-soeges-til-bm-maler-i-vejle"
                  className="inline-flex items-center gap-2 bg-[#F1B528] hover:bg-[#D99C1B] text-slate-950 px-5 py-2.5 rounded-full text-xs font-bold transition-all duration-300 shadow-sm"
                >
                  Se ledige stillinger
                  <ArrowRight className="w-3.5 h-3.5" />
                </RouterLink>
              </div>
            </motion.div>

          </motion.div>
        </div>
      </section>

      {/* SECTION 7: Infinite Review Slider with premium styling */}
      <section className="py-12 bg-slate-950 text-white relative overflow-hidden">
        <div className="absolute inset-0 z-0 opacity-10">
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#F1B528] rounded-full blur-3xl" />
        </div>
        <div className="relative z-10">
          <ReviewSlider />
        </div>
      </section>

      {/* SECTION 8: Contact Section with asymmetrical split card */}
      <section id="kontaktformular" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 scroll-mt-12">
        <motion.div 
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="bg-slate-900 rounded-3xl overflow-hidden border border-slate-800 shadow-2xl"
        >
          <div className="grid grid-cols-1 lg:grid-cols-12 items-stretch">
            {/* Info Sidebar (Dark Slate) */}
            <div className="lg:col-span-5 bg-slate-950 p-8 sm:p-12 flex flex-col justify-between relative overflow-hidden text-white">
              <div className="absolute inset-0 z-0 opacity-20">
                <div className="absolute top-1/2 right-0 w-64 h-64 bg-[#F1B528] rounded-full blur-3xl" />
              </div>

              <div className="relative z-10 space-y-8">
                <div className="space-y-4">
                  <span className="text-[#F1B528] text-xs font-bold uppercase tracking-wider bg-white/10 border border-white/20 px-3 py-1 rounded-full">
                    Kontakt os i dag
                  </span>
                  <h2 className="text-3xl sm:text-4xl font-bold tracking-tight leading-none pt-6">
                    Lad os klare dit malerarbejde – ordentligt fra start til slut
                  </h2>
                  <p className="text-slate-400 text-xs sm:text-sm leading-relaxed">
                    Vi sidder klar til at rådgive dig og give en pris på din maleropgave. Vores faglærte malere leverer altid til tiden.
                  </p>
                </div>

                <ul className="space-y-6">
                  <li className="flex items-start gap-4">
                    <div className="p-3 rounded-full bg-white/5 text-[#F1B528] shrink-0">
                      <Phone className="w-5 h-5" />
                    </div>
                    <div>
                      <span className="block text-slate-500 text-[10px] font-bold uppercase tracking-wider">Ring direkte</span>
                      <a href="tel:+4575695900" className="text-base font-bold text-[#F1B528] hover:text-[#D99C1B] transition-colors whitespace-nowrap">75 69 59 00</a>
                    </div>
                  </li>

                  <li className="flex items-start gap-4">
                    <div className="p-3 rounded-full bg-white/5 text-[#F1B528] shrink-0">
                      <Mail className="w-5 h-5" />
                    </div>
                    <div>
                      <span className="block text-slate-500 text-[10px] font-bold uppercase tracking-wider">Send en e-mail</span>
                      <a href="mailto:bm@bmmaler.dk" className="text-base font-bold text-[#F1B528] hover:text-[#D99C1B] transition-colors whitespace-nowrap">bm@bmmaler.dk</a>
                    </div>
                  </li>

                  <li className="flex items-start gap-4">
                    <div className="p-3 rounded-full bg-white/5 text-[#F1B528] shrink-0">
                      <MapPin className="w-5 h-5" />
                    </div>
                    <div>
                      <span className="block text-slate-500 text-[10px] font-bold uppercase tracking-wider">Besøg os</span>
                      <span className="text-base font-bold text-white">Håndværkervej 3A, 7120 Vejle Øst</span>
                    </div>
                  </li>
                </ul>
              </div>

              <div className="relative z-10 pt-12 border-t border-slate-800/80 mt-12 flex items-center gap-4">
                <img
                  src={getOptimizedImageUrl("https://assets.auxodev.dk/assets/b3b17412-91e4-45b1-be58-3b3c80af2fb2/69cdfa93-d212-46d0-9d1c-ba4195739c50.png", { width: 160, quality: 85 })}
                  alt="Danske Malermestre Garantiordning Logo"
                  width="160"
                  height="40"
                  loading="lazy"
                  decoding="async"
                  className="h-10 w-auto object-contain"
                />
                <div className="text-xs text-slate-400">
                  <span className="block font-bold text-white">Garantiordning</span>
                  <span>Medlem af Danske Malermestre</span>
                </div>
              </div>
            </div>

            {/* Form Side (Slate 900) */}
            <div className="lg:col-span-7 p-8 sm:p-12 flex items-center justify-center bg-slate-900">
              <div className="w-full max-w-xl">
                <ContactForm />
              </div>
            </div>
          </div>
        </motion.div>
      </section>
    </div>
  );
}
